import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CollapseBlock,
  Spinner,
  QuestionModal,
} from '../../../../common/index.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import EduParticipantsTable from './EduParticipantsTable.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import {
  useGetEduCourseDateParticipantsQuery,
  useUnenrollFromEduCourseDateMutation,
  useSendEduCourseDateMessageMutation,
} from '../../../../../redux/services/eduCourseDates.jsx';
import dayjs from 'dayjs';
import { DATETIME_FORMAT_DATE } from '../../../../../utils/helpers/CommonHelper.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import Form from '../../../../antd/form/Form/Form.jsx';
import { Form as AntForm } from 'antd';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import FormTextArea from '../../../../antd/form/FormTextArea/FormTextArea.jsx';
import {
  StyledModal,
  StyledModalHeader,
  ButtonContainer,
  MainButton,
} from '../../../../common/form/FormElements.jsx';
import {
  MailOutlined,
  TeamOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { isUserAndItsMorphIdentical } from '../../../../../utils/helpers/CommonHelper.jsx';
import { useSelector } from 'react-redux';
import { empty } from '../../../../antd/form/FormValidation.js';
import api from '../../../../../utils/helpers/bootstrapApi.jsx';

const EduParticipantsPage = () => {
  const { t } = useTranslation();
  const { dateId } = useParams();

  const {
    data: dateData,
    isLoading,
    refetch,
  } = useGetEduCourseDateParticipantsQuery(dateId);
  const [unenrollFromCourseDate] = useUnenrollFromEduCourseDateMutation();
  const [sendMessage] = useSendEduCourseDateMessageMutation();
  const { successMessage, apiErrorMessage } = useToastMessages();

  const [rowToUnenroll, setRowToUnenroll] = useState(null);
  const [mailModalOpen, setMailModalOpen] = useState(false);

  const from = dateData?.course_date?.date_from
    ? dayjs(dateData.course_date.date_from).format(DATETIME_FORMAT_DATE)
    : '';
  const to = dateData?.course_date?.date_to
    ? dayjs(dateData.course_date.date_to).format(DATETIME_FORMAT_DATE)
    : '';
  const termRange = from && to ? `${from} - ${to}` : from || to || '';
  const title = termRange
    ? `${t('Účastníci termínu')} ${termRange}`
    : t('Účastníci termínu');

  // Set CMS page title
  useEffect(() => {
    document.title = 'FRI.edu - ' + t('Účastníci termínu');
  }, [t]);

  const confirmUnenroll = async () => {
    if (!rowToUnenroll) return;
    try {
      await unenrollFromCourseDate({
        dateId,
        participantId: rowToUnenroll.id,
        byLecturer: true,
      }).unwrap();
      successMessage({ message: t('Odhlásenie z termínu prebehlo úspešne') });
      await refetch?.();
    } catch (e) {
      apiErrorMessage(e);
    } finally {
      setRowToUnenroll(null);
    }
  };

  const [mailForm] = AntForm.useForm();
  const submitBroadcast = async (values) => {
    try {
      await sendMessage({
        dateId,
        subject: values.subject,
        body: values.body,
      }).unwrap();
      successMessage({ message: t('Správa bola odoslaná všetkým účastníkom') });
      setMailModalOpen(false);
      mailForm.resetFields();
    } catch (e) {
      apiErrorMessage(e);
    }
  };

  // Prefill subject when opening the modal
  useEffect(() => {
    if (!mailModalOpen) return;
    const courseName = dateData?.course?.name || '';
    const subject = t(`Informácie o termíne ${termRange} kurzu ${courseName}`);
    try {
      mailForm.setFieldsValue({ subject });
    } catch {}
  }, [mailModalOpen, termRange, dateData?.course?.name, t]);

  const user = useSelector((s) => s.auth.user);
  const isAuthor = isUserAndItsMorphIdentical(
    user,
    dateData?.course?.lecturerable,
  );

  // PDF download handler
  const handleDownloadParticipantsPdf = async () => {
    try {
      const url = `/api/fri-edu/course-dates/${dateId}/participants-pdf`;

      const response = await api({
        url,
        method: 'GET',
      });

      // Decode Base64 string to binary
      const byteCharacters = atob(response.data.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download =
        response.data.filename ||
        `Zoznam_ucastnikov_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      apiErrorMessage(error);
    }
  };

  // Attendance sheet download handler
  const handleDownloadAttendanceSheet = async () => {
    try {
      const url = `/api/fri-edu/course-dates/${dateId}/attendance-sheet-pdf`;

      const response = await api({
        url,
        method: 'GET',
      });

      // Decode Base64 string to binary
      const byteCharacters = atob(response.data.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download =
        response.data.filename ||
        `Prezencna_listina_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading attendance sheet:', error);
      apiErrorMessage(error);
    }
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={title}
        backUrl={`${routes.cmsCoursesList}`}
        backUrlTooltip={t('Zoznam kurzov')}
        permissionsBasic={'fri-edu-course-dates'}
        mainActions={[
          {
            title: t('Poslať e‑mail účastníkom'),
            onClick: () => setMailModalOpen(true),
            icon: <MailOutlined />,
            permission: 'fri-edu-course-dates.update',
            disabled: !isAuthor,
            disabledTooltip: t('Správu môže poslať len autor kurzu'),
          },
          {
            title: t('Stiahnuť zoznam účastníkov'),
            onClick: handleDownloadParticipantsPdf,
            icon: <TeamOutlined />,
            permission: 'fri-edu-course-dates.show',
          },
          {
            title: t('Stiahnuť prezenčnú listinu'),
            onClick: handleDownloadAttendanceSheet,
            icon: <SolutionOutlined />,
            permission: 'fri-edu-course-dates.show',
          },
        ]}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock title={t('Názov kurzu')} collapsible={true}>
          {dateData?.course?.name && (
            <div style={{ fontSize: '17px' }}>{dateData.course.name}</div>
          )}
        </CollapseBlock>
        <CollapseBlock
          title={t('Zoznam účastníkov termínu')}
          collapsible={true}
        >
          <EduParticipantsTable
            participants={
              Array.isArray(dateData?.participants) ? dateData.participants : []
            }
            onUnenroll={(row) => setRowToUnenroll(row)}
          />
        </CollapseBlock>
      </div>

      <QuestionModal
        visible={rowToUnenroll !== null}
        toggle={() => setRowToUnenroll(null)}
        chosenPositive={confirmUnenroll}
        headerTitle={t('Odhlásiť účastníka')}
        question={t('Naozaj chcete odhlásiť tohto účastníka z termínu?')}
        yes={t('Áno')}
      />

      <StyledModal
        open={mailModalOpen}
        size="md"
        onCancel={() => setMailModalOpen(false)}
        footer={null}
      >
        <StyledModalHeader>{t('Poslať správu účastníkom')}</StyledModalHeader>
        <Form form={mailForm} onFinish={submitBroadcast} initialValues={{}}>
          <FormTextField
            name="subject"
            label={t('Predmet')}
            rules={[empty(t)]}
          />
          <FormTextArea
            name="body"
            label={t('Správa')}
            rules={[empty(t)]}
            rows={8}
          />
          <ButtonContainer>
            <MainButton onClick={() => setMailModalOpen(false)}>
              {t('Zrušiť')}
            </MainButton>
            <MainButton htmlType="submit">{t('Odoslať')}</MainButton>
          </ButtonContainer>
        </Form>
      </StyledModal>
    </>
  );
};

export default EduParticipantsPage;
